import React, { useCallback, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type FieldValues, type Path } from 'react-hook-form';
import { z, type ZodTypeAny } from 'zod';
import type { AsyncStatus, StepContext, WizardProps } from './types';
import { clearWizardPersistence, useWizardPersistence } from './useWizardPersistence';
import './Wizard.css';

function composeSchema(steps: { schema: ZodTypeAny }[]): ZodTypeAny {
  return steps.reduce<ZodTypeAny>((acc, step) => {
    return z.intersection(acc, step.schema);
  }, z.object({}));
}

export function Wizard<TValues extends FieldValues>(
  props: WizardProps<TValues>,
): React.ReactElement {
  const {
    steps,
    defaultValues,
    onFinish,
    onCancel,
    persistKey,
    persistExclude,
    title,
    submitLabel = 'Fertig',
  } = props;

  const resolver = useMemo(() => zodResolver(composeSchema([...steps])), [steps]);
  const form = useForm<TValues>({
    defaultValues: defaultValues as never,
    resolver,
    mode: 'onChange',
  });

  useWizardPersistence(form, persistKey, persistExclude);

  const [activeStep, setActiveStep] = useState(0);
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('idle');
  const [asyncError, setAsyncError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const step = steps[activeStep];
  const isFirst = activeStep === 0;
  const isLast = activeStep === steps.length - 1;

  const runGuard = useCallback(async (): Promise<boolean> => {
    const fieldsToValidate = step.fields as Path<TValues>[];
    const valid = fieldsToValidate.length === 0
      ? true
      : await form.trigger(fieldsToValidate, { shouldFocus: true });
    if (!valid) return false;
    if (!step.asyncGuard) return true;

    setAsyncStatus('running');
    setAsyncError(null);
    try {
      await step.asyncGuard(form.getValues(), form);
      setAsyncStatus('success');
      return true;
    } catch (err) {
      setAsyncStatus('error');
      setAsyncError(err instanceof Error ? err.message : 'Schritt fehlgeschlagen');
      return false;
    }
  }, [form, step]);

  const goNext = useCallback(async () => {
    const ok = await runGuard();
    if (!ok) return;
    if (isLast) {
      setSubmitting(true);
      try {
        await onFinish(form.getValues());
        clearWizardPersistence(persistKey);
      } catch (err) {
        setAsyncStatus('error');
        setAsyncError(err instanceof Error ? err.message : 'Abschicken fehlgeschlagen');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setAsyncStatus('idle');
    setAsyncError(null);
    setActiveStep((s) => s + 1);
  }, [form, isLast, onFinish, persistKey, runGuard]);

  const goBack = useCallback(() => {
    setAsyncStatus('idle');
    setAsyncError(null);
    setActiveStep((s) => Math.max(0, s - 1));
  }, []);

  const retry = useCallback(() => {
    setAsyncStatus('idle');
    setAsyncError(null);
  }, []);

  const ctx: StepContext = {
    goNext: () => void goNext(),
    goBack,
    isFirst,
    isLast,
    asyncStatus,
    asyncError,
    retry,
  };

  const working = asyncStatus === 'running' || submitting;

  return (
    <section className="wizard" aria-label={title ?? 'Wizard'}>
      {title && <h2 className="wizard-title">{title}</h2>}
      <ol className="wizard-stepper">
        {steps.map((s, idx) => {
          const state =
            idx < activeStep
              ? 'wizard-step-complete'
              : idx === activeStep
                ? 'wizard-step-active'
                : '';
          return (
            <li key={s.id} className={`wizard-step ${state}`} aria-current={idx === activeStep ? 'step' : undefined}>
              <span className="wizard-step-badge">{idx + 1}</span>
              <span>{s.label}</span>
              {idx < steps.length - 1 && <span className="wizard-step-separator">›</span>}
            </li>
          );
        })}
      </ol>
      <div className="wizard-body">
        {step.description && <p className="wizard-description">{step.description}</p>}
        {step.render(form, ctx)}
        <div aria-live="polite">
          {asyncError && (
            <div className="wizard-error">
              <span>{asyncError}</span>
              <button type="button" className="wizard-error-retry" onClick={retry}>Erneut versuchen</button>
            </div>
          )}
        </div>
      </div>
      <div className="wizard-actions">
        {onCancel && (
          <button type="button" className="wizard-btn wizard-btn-secondary" onClick={onCancel} disabled={working}>
            Abbrechen
          </button>
        )}
        <button type="button" className="wizard-btn wizard-btn-secondary" onClick={goBack} disabled={isFirst || working}>
          Zurück
        </button>
        <button
          type="button"
          className="wizard-btn wizard-btn-primary"
          onClick={() => void goNext()}
          disabled={working}
        >
          {working ? 'Bitte warten…' : isLast ? submitLabel : 'Weiter'}
        </button>
      </div>
    </section>
  );
}

export { clearWizardPersistence } from './useWizardPersistence';
export type { WizardProps, WizardStep, StepContext, AsyncStatus } from './types';
